// src/utilities/inviteAuth.js — SOUND-752
//
// WHAT AN INVITE LINK DOES ON THE AUTH PAGES. One answer, used by AuthSection,
// AuthFooter and AuthForm alike.
//
// Before this, an invitation was only ever looked at on /auth/signup, and every
// route OFF that page lost it. Four dead ends, all observed in prod:
//
//   1. Invite link, but a Firebase account already exists for the address →
//      auth/email-already-in-use → "use a different email". The invite email is
//      read-only, so there was nothing to do. (angelica@, 2026-09-16)
//   2. The "Sign in" footer link was a bare /auth/signin and DROPPED ?invite=.
//      Sign-in then took the self-serve branch and asked an internal hire for a
//      business name — typing one made her a CLIENT of a junk company.
//      (jakob@, 2026-09-29, who typed one)
//   3. /auth/signin?invite=<token> spun on "Loading invitation..." forever: the
//      token was only validated on signup, but the spinner did not know that.
//   4. Reopening an ALREADY-ACCEPTED invite rendered the "already been used"
//      banner and then the SELF-SERVE signup form underneath it. Re-entering the
//      address hit dead end 1. (matt+adguardians@, 2026-10-23 — his account was
//      fine; he only needed to sign in)
//
// And one hazard: POST /users fails CLOSED on a dead token, deleting the
// Firebase account when no users doc exists. So a dead token must never reach
// it from sign-in, where that account is the thing the person is signing in to.
//
// Pure because sound-app has NO component-test harness (SOUND-679).

/** Invitation tokens are 64 hex chars; anything else is a legacy data link. */
export const INVITE_TOKEN_LENGTH = 64;

/** Only these pages read an invitation. forgotpass/changepass ignore it. */
const INVITE_AWARE_TYPES = ["signup", "signin"];

export function isInvitationToken(param) {
  return typeof param === "string" && param.length === INVITE_TOKEN_LENGTH;
}

/**
 * Append the invite to an auth path, so moving between signup and signin keeps
 * it (dead end 2). A falsy token returns the path untouched.
 */
export function withInvite(path, token) {
  if (!token) return path;
  return `${path}?invite=${encodeURIComponent(token)}`;
}

/**
 * Where the page stands with its invitation.
 *
 * @param {object}  args
 * @param {string}  args.type      the auth page type
 * @param {?string} args.token     the ?invite= param, if any
 * @param {?object} args.context   the /invitations/validate payload, once it answered OK
 * @param {?string} args.error     the refusal message, once it answered not-OK
 * @returns {{
 *   state: "none" | "loading" | "valid" | "dead",
 *   validate: boolean,    should the page call /invitations/validate
 *   showForm: boolean,    should the auth form render at all
 *   dropToken: boolean,   should ?invite= be removed from the URL
 *   linkToken: ?string,   the token footer links must carry
 * }}
 */
export function inviteView({ type, token, context = null, error = null } = {}) {
  const none = {
    state: "none",
    validate: false,
    showForm: true,
    dropToken: false,
    linkToken: null,
  };

  if (!INVITE_AWARE_TYPES.includes(type) || !isInvitationToken(token)) return none;

  if (error) {
    return {
      state: "dead",
      validate: false,
      // Dead end 4. A signup form under a dead invite is the SELF-SERVE form —
      // email editable, business name asked — which is the one outcome an
      // invitation exists to prevent. Sign-in is harmless, so it stays.
      showForm: type !== "signup",
      // The hazard. auth.js reads the token straight off the URL and sends it
      // to POST /users, which deletes an unprovisioned account on a dead token.
      // Signup hides its form instead, so only sign-in can reach that call.
      dropToken: type === "signin",
      linkToken: null,
    };
  }

  if (context) {
    return { state: "valid", validate: false, showForm: true, dropToken: false, linkToken: token };
  }

  // Dead end 3 was a spinner on a page that never validated. Loading and
  // validating are now the same condition, so they cannot disagree again.
  return { state: "loading", validate: true, showForm: false, dropToken: false, linkToken: null };
}

/**
 * Dead end 1: an invited signup whose address already has a Firebase account.
 * The fix is to sign in, never to "use a different email".
 */
export function isExistingAccountError(error) {
  return (
    error?.code === "auth/email-already-in-use" ||
    String(error?.message || "").includes("auth/email-already-in-use")
  );
}
